/* =============================================================================
 * ICONOS Y ORNAMENTOS DE BOGOTÁ · SVG dibujado, nunca imagen.
 * -----------------------------------------------------------------------------
 * Son composición de ESTA página (ADR-004 §1.3: un ornamento geométrico no se
 * genera como imagen). Los trazos salen de `docs/sections/DELTA_ICONOS_VERIFICADOS.html`,
 * ya mirados en navegador antes de escribir una línea de React.
 *
 * El ornamento firma de Bogotá es el CORAZÓN dorado de trazo fino: aparece en el
 * divisor del hero, en la onda inferior, en dos de los cuatro beneficios, en el
 * divisor de la Cámara, en las tres cabeceras de la zona baja y en el mantra.
 *
 * Todos son decorativos (`aria-hidden`): el significado va SIEMPRE en el texto
 * real que acompaña al icono, nunca en el dibujo.
 * ========================================================================== */
import styles from './BogotaIcons.module.css';

interface IconProps {
  size?: number;
  className?: string;
}

/** 1 · Corazón macizo con ráfaga de rayos — «Sanación profunda». */
export function IconCorazonRafaga({ size = 58, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles.hairline}>
        <path d="M32 4v7M32 53v7M4 32h7M53 32h7M12.2 12.2l5 5M46.8 46.8l5 5M51.8 12.2l-5 5M17.2 46.8l-5 5" />
        <path d="M20.5 6.6l2.6 6.3M40.9 51.1l2.6 6.3M6.6 43.5l6.3-2.6M51.1 23.1l6.3-2.6M6.6 20.5l6.3 2.6M51.1 40.9l6.3 2.6M20.5 57.4l2.6-6.3M40.9 12.9l2.6-6.3" />
      </g>
      <path
        className={styles.fill}
        d="M32 45.5c-1 0-11.8-7.2-14.8-13.1-2.4-4.7-.8-10 3.9-11.7 3.7-1.3 7.6.3 9.4 3.4l1.5 2.5 1.5-2.5c1.8-3.1 5.7-4.7 9.4-3.4 4.7 1.7 6.3 7 3.9 11.7C43.8 38.3 33 45.5 32 45.5Z"
      />
    </svg>
  );
}

/** 2 · Arcos concéntricos sobre ondas de montaña — «Frecuencia Origen». */
export function IconArcosOndas({ size = 58, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <circle className={styles.stroke} cx="32" cy="32" r="21" />
      <path className={styles.hairline} d="M18 30a14 14 0 0 1 28 0" />
      <path className={styles.hairline} d="M22 30a10 10 0 0 1 20 0" />
      <path className={styles.hairline} d="M26 30a6 6 0 0 1 12 0" />
      <path className={styles.stroke} d="M13.5 36.5c3.5-4.5 6.5-4.5 10 0s6.5 4.5 10 0 6.5-4.5 10 0" />
      <path className={styles.stroke} d="M14.5 42.5c3.2-4 6-4 9.2 0s6 4 9.2 0 6-4 9.2 0" />
      <path className={styles.hairline} d="M18 48c2.8-3.2 5.2-3.2 8 0s5.2 3.2 8 0 5.2-3.2 8 0" />
    </svg>
  );
}

/** 3 · Flor de la vida / roseta de 6 pétalos — «Linaje estelar». */
export function IconFlorDeLaVida({ size = 58, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <circle className={styles.stroke} cx="32" cy="32" r="21" />
      <g className={styles.hairline}>
        <circle cx="32" cy="32" r="10.5" />
        <circle cx="32" cy="21.5" r="10.5" />
        <circle cx="32" cy="42.5" r="10.5" />
        <circle cx="41.1" cy="26.75" r="10.5" />
        <circle cx="41.1" cy="37.25" r="10.5" />
        <circle cx="22.9" cy="26.75" r="10.5" />
        <circle cx="22.9" cy="37.25" r="10.5" />
      </g>
    </svg>
  );
}

/** 4 · Corazón con ráfaga larga — «Recordar quién eres». */
export function IconCorazonRafagaAlt({ size = 58, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 64 64"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles.hairline}>
        <path d="M32 2v9M32 53v9M2 32h9M53 32h9M10.8 10.8l6.4 6.4M46.8 46.8l6.4 6.4M53.2 10.8l-6.4 6.4M17.2 46.8l-6.4 6.4" />
        <path d="M17.5 4.9l3.4 8.2M43.1 50.9l3.4 8.2M4.9 46.5l8.2-3.4M50.9 20.9l8.2-3.4M4.9 17.5l8.2 3.4M50.9 43.1l8.2 3.4M17.5 59.1l3.4-8.2M43.1 13.1l3.4-8.2" />
      </g>
      <path
        className={styles.fill}
        d="M32 45c-.9 0-11.2-6.9-14.1-12.5-2.3-4.5-.8-9.5 3.7-11.1 3.5-1.2 7.2.3 8.9 3.2l1.5 2.4 1.5-2.4c1.7-2.9 5.4-4.4 8.9-3.2 4.5 1.6 6 6.6 3.7 11.1C43.2 38.1 32.9 45 32 45Z"
      />
    </svg>
  );
}

/** Corazón suelto de contorno: el remate de los divisores y de las cabeceras. */
export function CorazonContorno({ size = 18, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={Math.round(size * 0.86)}
      viewBox="0 0 22 19"
      aria-hidden="true"
      focusable="false"
    >
      <path
        className={styles.stroke}
        d="M11 17c-.6 0-7.3-4.5-9.2-8.2C.3 5.9 1.3 2.6 4.2 1.5 6.5.7 8.9 1.7 10 3.6l1 1.6 1-1.6c1.1-1.9 3.5-2.9 5.8-2.1 2.9 1.1 3.9 4.4 2.4 7.3C18.3 12.5 11.6 17 11 17Z"
      />
    </svg>
  );
}

/**
 * Divisor firma: filete ─ ♡ ─ . Es el ornamento que más se repite en la página.
 * `width` en px del viewBox; se estira al 100 % del hueco disponible.
 */
export function DividerCorazon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 300 24"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <path className={styles.hairline} d="M8 12h122M170 12h122" />
      <path
        className={styles.stroke}
        d="M150 19c-.6 0-7.3-4.5-9.2-8.2-1.5-2.9-.5-6.2 2.4-7.3 2.3-.8 4.7.2 5.8 2.1l1 1.6 1-1.6c1.1-1.9 3.5-2.9 5.8-2.1 2.9 1.1 3.9 4.4 2.4 7.3-1.9 3.7-8.6 8.2-9.2 8.2Z"
      />
    </svg>
  );
}

/** Onda doble dorada con corazón centrado: pie del hero y remate del mantra. */
export function OndaCorazon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 300 40"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <path
        className={styles.hairline}
        d="M0 22c30-14 60-14 90 0s60 14 90 0 60-14 90 0 30 7 30 7"
      />
      <path
        className={styles.hairline}
        opacity=".65"
        d="M0 27c30-14 60-14 90 0s60 14 90 0 60-14 90 0 30 7 30 7"
      />
      <path
        className={styles.stroke}
        vectorEffect="non-scaling-stroke"
        d="M150 27c-.5 0-6-3.7-7.6-6.8-1.2-2.4-.4-5.1 2-6 1.9-.7 3.9.1 4.8 1.7l.8 1.3.8-1.3c.9-1.6 2.9-2.4 4.8-1.7 2.4.9 3.2 3.6 2 6-1.6 3.1-7.1 6.8-7.6 6.8Z"
      />
    </svg>
  );
}

/** Medallón de sol: hito del DÍA 1 del cronograma. */
export function IconSol({ size = 66, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 72 72"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse className={styles.medallion} cx="36" cy="36" rx="27" ry="31" />
      <circle className={styles.fill} cx="36" cy="36" r="8" />
      <g className={styles.hairline}>
        <path d="M36 17v6M36 49v6M17 36h6M49 36h6M23.5 23.5l4.2 4.2M44.3 44.3l4.2 4.2M48.5 23.5l-4.2 4.2M27.7 44.3l-4.2 4.2" />
      </g>
    </svg>
  );
}

/** Medallón de luna con estrellitas: hito del DÍA 2 del cronograma. */
export function IconLuna({ size = 66, className }: IconProps) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 72 72"
      aria-hidden="true"
      focusable="false"
    >
      <ellipse className={styles.medallion} cx="36" cy="36" rx="27" ry="31" />
      <path
        className={styles.navyFill}
        d="M42.5 22a15.5 15.5 0 1 0 0 28 18 18 0 0 1 0-28Z"
      />
      <path
        className={styles.navyFill}
        d="M50 26.5l1.1 2.7 2.7 1.1-2.7 1.1-1.1 2.7-1.1-2.7-2.7-1.1 2.7-1.1zM52 41l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8zM45.5 47.5l.6 1.5 1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6z"
      />
    </svg>
  );
}

/**
 * SELLO DEL HERO · el aro del medallón que el mockup dibuja alrededor de la marca.
 * -----------------------------------------------------------------------------
 * OJO CON LO QUE ES Y CON LO QUE NO ES. Esto es el ORNAMENTO: aro doble de trazo
 * fino, corazón encaramado arriba partiendo el aro, y dos abanicos de rayos
 * —uno abierto hacia arriba y otro hacia abajo— alrededor del hueco central.
 * Medido sobre el póster: medallón de 161x163 px a 1024 de ancho, o sea casi un
 * círculo, con el rótulo interior ocupando ~110 px (68 % del diámetro).
 *
 * LA MARCA NO SE DIBUJA AQUÍ. El póster rotula «AOGR» dentro del aro y eso es una
 * errata de la generación de imagen (ADR-002, VISUAL_DEVIATIONS A1): el rótulo
 * que va dentro es el LOGO OFICIAL en PNG, que lo pone `HeroBogota` encima de
 * este aro. Aquí no hay ni una letra, y es a propósito.
 *
 * Decorativo: `aria-hidden`. El nombre accesible lo lleva la imagen del logo.
 */
export function SelloMedallon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 200 200"
      aria-hidden="true"
      focusable="false"
    >
      {/* Aro doble. El de fuera es el que manda; el de dentro respira a 3,5 px. */}
      <circle className={styles.hairline} cx="100" cy="101" r="76" />
      <circle className={styles.hairline} opacity=".55" cx="100" cy="101" r="72.5" />

      {/* Abanico superior: siete radios con sus arquillos, como el póster. */}
      <g className={styles.hairline} opacity=".8">
        <path d="M100 46v26M100 46v26" />
        <path d="M82 50l10 24M118 50l-10 24" />
        <path d="M65 58l16 21M135 58l-16 21" />
        <path d="M51 70l20 15M149 70l-20 15" />
        <path d="M82 50q18-9 36 0" />
        <path d="M65 58q35-19 70 0" />
      </g>

      {/* Abanico inferior, espejo del de arriba y más corto. */}
      <g className={styles.hairline} opacity=".62">
        <path d="M100 156v-22" />
        <path d="M84 152l8-20M116 152l-8-20" />
        <path d="M68 144l14-18M132 144l-14-18" />
        <path d="M84 152q16 8 32 0" />
      </g>

      {/* EL CORAZÓN, el motivo de esta página, encaramado en lo alto del aro.
          Va relleno de crema opaca para partir el aro por debajo, que es lo que
          hace el póster: el trazo del círculo no le pasa por dentro. */}
      <path
        d="M100 41c-.7 0-9.6-5.9-12.1-10.8-2-3.8-.7-8.2 3.2-9.6 3-1.1 6.2.3 7.7 2.8l1.2 2.1 1.2-2.1c1.5-2.5 4.7-3.9 7.7-2.8 3.9 1.4 5.2 5.8 3.2 9.6C109.6 35.1 100.7 41 100 41Z"
        fill="var(--az-scrim-photo)"
      />
      <path
        className={styles.stroke}
        d="M100 41c-.7 0-9.6-5.9-12.1-10.8-2-3.8-.7-8.2 3.2-9.6 3-1.1 6.2.3 7.7 2.8l1.2 2.1 1.2-2.1c1.5-2.5 4.7-3.9 7.7-2.8 3.9 1.4 5.2 5.8 3.2 9.6C109.6 35.1 100.7 41 100 41Z"
      />
    </svg>
  );
}

/**
 * ESTELAS DE LA CÁMARA · las tres cintas doradas que el póster pone entre el
 * texto y la escena de vidrio, y que faltaban.
 * -----------------------------------------------------------------------------
 * No son adorno libre: están medidas. En `DEMO_2_Bogota.png`, banda S03 (1024x257),
 * son tres curvas finísimas de oro pálido que barren de abajo-izquierda a
 * arriba-derecha por el hueco que queda entre la columna de texto y el biombo.
 * Trazadas sobre la máscara del recorte a 4x, la de en medio pasa por:
 *
 *     y   0 -> x 564   |   y  50 -> x 555   |   y 125 -> x 522
 *     y 175 -> x 500   |   y 257 -> x 475
 *
 * O sea una **ogiva**: suave arriba (−0,18 px/px), fuerte en el centro (−0,44) y
 * suave otra vez abajo (−0,30). Una recta no sirve; una sola Bézier tampoco. Las
 * otras dos van paralelas a ±11 px. El `viewBox` es 140x257 y arranca en x 455
 * del póster, así que las coordenadas de arriba menos 455 son las del path.
 *
 * `preserveAspectRatio="none"`: la banda real mide 279 px de alto y no 257, y lo
 * que se calca es el BARRIDO, no la longitud del arco.
 *
 * Los extremos salen y entran FUERA del viewBox (y −6 y y 263) a propósito: en el
 * póster las cintas no empiezan ni acaban dentro de la banda, la cruzan. Cortarlas
 * dentro dejaba dos puntas visibles a media altura.
 */
export function EstelasCamara({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 140 257"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles.thread}>
        <path d="M120 -6C116 36 99 82 78 122c-21 40-40 95-47 141" opacity=".9" />
        <path d="M109 -6C105 34 88 80 67 120c-21 40-40 95-47 143" />
        <path d="M132 -6C128 32 111 78 90 118c-21 40-40 95-47 145" opacity=".7" />
      </g>
    </svg>
  );
}

/** Hilos dorados ondulados que cruzan el hero por arriba y por abajo. */
export function HilosHero({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 1440 900"
      preserveAspectRatio="none"
      aria-hidden="true"
      focusable="false"
    >
      <g className={styles.thread}>
        <path d="M-40 132C160 46 320 60 470 128" />
        <path d="M-40 168C170 78 330 92 486 160" opacity=".6" />
        <path d="M-60 742c220-118 440-118 660 0s440 118 660 0 240 44 240 44" />
        <path d="M-60 786c220-118 440-118 660 0s440 118 660 0 240 44 240 44" opacity=".62" />
        <path d="M-60 826c220-108 440-108 660 0s440 108 660 0 240 40 240 40" opacity=".4" />
      </g>
    </svg>
  );
}
